import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { Button } from '../ui/button';

interface AnalyticsConsentBannerProps {
  onAccept: () => void;
  onDecline: () => void;
}

/**
 * Opt-in analytics consent banner (OMN-360). Shown once, until the user makes a
 * choice. Nothing is captured by PostHog until "Accept" is pressed.
 */
export const AnalyticsConsentBanner: React.FC<AnalyticsConsentBannerProps> = ({ onAccept, onDecline }) => {
  const { t } = useLanguage();

  return (
    <div
      role="dialog"
      aria-modal="false"
      aria-label={t('analytics.consent.title', { defaultValue: 'Analytics consent' })}
      className="fixed inset-x-0 bottom-0 z-[60] p-3 sm:p-4"
    >
      <div className="mx-auto max-w-3xl rounded-lg border border-gray-200 bg-white p-4 shadow-lg">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="text-sm text-gray-700">
            <p className="font-medium text-gray-900">
              {t('analytics.consent.title', { defaultValue: 'Help us improve OmniFlyer' })}
            </p>
            <p className="mt-1">
              {t('analytics.consent.message', {
                defaultValue:
                  'We use privacy-friendly product analytics to understand how the app is used. No personal data (like your email) is shared, and you can decline.',
              })}{' '}
              <Link to="/privacy" className="text-blue-600 underline hover:text-blue-800">
                {t('analytics.consent.learnMore', { defaultValue: 'Privacy Policy' })}
              </Link>
            </p>
          </div>
          <div className="flex shrink-0 gap-2">
            <Button variant="outline" onClick={onDecline} className="whitespace-nowrap">
              {t('analytics.consent.decline', { defaultValue: 'Decline' })}
            </Button>
            <Button onClick={onAccept} className="whitespace-nowrap">
              {t('analytics.consent.accept', { defaultValue: 'Accept' })}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
